<template lang="pug">
component(v-bind="wrapperOptions")
    | {{ formattedContent }}
</template>

<script>
import { variableExtractor, variableReplacer } from '@/helpers/templateTranslator'
import { formatCents } from '@/helpers/filters'
import UniversalLink from '@/components/universalLink'
import { generateFontSizeClass } from '../../../helpers'

// Singleton var to keep track when we are hitting algolia.
let searchTimeout = null

export default {
    name: 'Content',
    props: {
        data: {
            type: Object,
            default() {
                return {}
            },
        },
        linkColor: {
            type: String,
            default: '',
        },
    },
    computed: {
        wrapperOptions() {
            const options = {
                is: 'span',
                class: [],
            }

            if (this.data.marks) {
                this.data.marks.forEach((mark) => {
                    if (mark.type === 'styled')
                        options.class.push(generateFontSizeClass(mark.attrs?.class))
                    else if (mark.type === 'bold') options.class.push('font-bold')
                    else if (mark.type === 'italic') options.class.push('italic')
                    else if (mark.type === 'strike') options.class.push('line-through')
                    else if (mark.type === 'link') {
                        options.is = UniversalLink
                        // Storyblok appends real path to the beginning of Internal Links.
                        // In staging real path is undefined so this will not work unless a real path
                        // is hardcoded or undefined is replaced i.e. mark.attrs.href.replace('undefined', '')
                        options.link = mark.attrs.href
                        if (mark.attrs.linktype === 'story' && mark.attrs.anchor)
                            options.link = `${options.link}#${mark.attrs.anchor}`
                        options.openNewWindow = mark.attrs?.target === '_blank'
                        if (mark.attrs.linktype === 'email') options.link = `mailto:${options.link}`

                        // IF linkColor is an empty string it was set to Inherit, so we default it to Action Dark
                        // IF linkColor is null, it is old data that is missing the linkColor property and needs to have hasColorOverride be set to true
                        // IF linkColor is anything else, its a color and should be applied with hasColorOverride set to true
                        if (this.linkColor !== '') {
                            options.hasColorOverride = true
                            if (this.linkColor) {
                                options.class.push(this.linkColor)
                            }
                        } else options.class.push('text-action-dark')

                        // We need to default the element to a button because
                        // in this context, a div cannot be inside of a p tag. This will cause hydration issues
                        // buttons are more appropriate in the situation
                        options.element = 'button'
                    }
                })
            }

            return options
        },
        // Get price tokens
        priceTokens() {
            return this.tokens
                .filter(
                    (token) => token.startsWith('PRICE|', 1) || token.startsWith('LIST_PRICE|', 1)
                )
                .map((token) => token.slice(1, -1))
        },
        // Creates obj with the price tokens and their corresponding value
        priceReplacementValues() {
            return this.priceTokens.reduce((acc, token) => {
                const [type, productLineSlug] = token.split('|')

                const productRecord = this.$search.products.state.results.find(
                    (record) => productLineSlug === record.productLineSlug
                )

                let price = '$###.##'

                if (productRecord) {
                    switch (type) {
                        case 'LIST_PRICE':
                            price = formatCents(productRecord.originalPrice)
                            break

                        case 'PRICE':
                        default:
                            price = formatCents(productRecord.minimumPrice)
                            break
                    }
                }

                acc[token] = price

                return acc
            }, {})
        },
        replacementValues() {
            return {
                SITE: this.$config.runtime.siteName,
                DOMAIN: this.$config.runtime.domain,
                PHONE: this.$sitewideData.config.phoneGeneral,
                ...(!this.$storyblok.isEditing && {
                    FITMENT: this.$store.getters['fitment/fitmentFormatted'],
                }),
                ...this.priceReplacementValues,
            }
        },
        tokens() {
            return variableExtractor(this.data.text)
        },
        formattedContent() {
            return variableReplacer(this.data.text, this.tokens, this.replacementValues)
        },
    },
    watch: {
        // NOTE: This watcher only fires when in editmode because thats when the content is changing
        tokens() {
            this.parsePriceVariables()
        },
    },
    mounted() {
        this.parsePriceVariables()
    },
    methods: {
        parsePriceVariables() {
            if (this.priceTokens.length === 0) return

            this.priceTokens.forEach((priceToken) => {
                const [, productLineSlug] = priceToken.split('|')

                // If none, ignore
                if (!productLineSlug) return

                // Attempt to trigger the search
                this.addToSearchQueue(productLineSlug)
            })
        },
        addToSearchQueue(productLineSlug) {
            // Clear any existing timeouts
            clearTimeout(searchTimeout)

            // Add to search filters
            this.$search.products.addFilters('productLineSlug', productLineSlug)

            // Search for new products
            searchTimeout = setTimeout(() => {
                this.$search.products.setResultsPerPage(100).setQuery('').search()
                this.$search.products.clearFilters()
            }, 500)
        },
    },
}
</script>
